import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Footer from '../Components/Footer';
import LandingHeader from '../Components/LandingHeader';
import HttpInterceptor from '../lib/HttpInterceptor';
import { toast } from 'react-toastify';
import QuickActions from '../Components/QuickActions';
import {
  FaShoppingCart,
  FaHeart,
  FaShareAlt,
  FaStar,
  FaTruck,
  FaUndo,
  FaShieldAlt,
  FaStore,
  FaChevronLeft,
  FaChevronRight
} from 'react-icons/fa';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [isWishlisted, setIsWishlisted] = useState(false);

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await HttpInterceptor.get(`/api/products/${id}`);
      setProduct(response.data);

      // Fetch related products
      if (response.data.category) {
        const relatedResponse = await HttpInterceptor.get(`/api/products?category=${response.data.category}&limit=4`);
        setRelatedProducts(relatedResponse.data.filter(p => p._id !== id).slice(0, 4));
      }
    } catch (error) {
      toast.error('Product not found');
      navigate('/Market');
    } finally {
      setLoading(false);
    }
  };

  const addToCart = () => {
    try {
      const cart = JSON.parse(localStorage.getItem('marketplaceCart') || '[]');
      const existingItem = cart.find(item => item.productId === product._id);

      if (existingItem) {
        if (product.stock <= existingItem.quantity + quantity - 1) {
          toast.error('Insufficient stock');
          return;
        }
        const updatedCart = cart.map(item =>
          item.productId === product._id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
        localStorage.setItem('marketplaceCart', JSON.stringify(updatedCart));
      } else {
        if (product.stock < quantity) {
          toast.error('Insufficient stock');
          return;
        }
        const newCart = [...cart, {
          productId: product._id,
          title: product.title,
          price: product.price,
          discount: product.discount,
          image: product.image,
          gstRate: product.gstRate || 18,
          hsnCode: product.hsnCode || '',
          resellerState: product.resellerId?.address?.state || 'platform',
          quantity: quantity
        }];
        localStorage.setItem('marketplaceCart', JSON.stringify(newCart));
      }
      toast.success('Added to cart');
      // Trigger cart update event
      window.dispatchEvent(new Event('storage'));
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  const calculateDiscountedPrice = (price, discount) => {
    return price * (1 - discount / 100);
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: product.title,
          text: product.description,
          url: window.location.href,
        });
      } catch (err) {
        // User cancelled or error
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      toast.success('Link copied to clipboard!');
    }
  };

  const images = product?.images?.length > 0 ? product.images : (product?.image ? [product.image] : []);

  if (loading) {
    return (
      <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-gray-100">
        <LandingHeader />
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="flex justify-center items-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-gray-100">
        <LandingHeader />
        <main className="flex-1 container mx-auto px-4 py-8 text-center">
          <h1 className="text-2xl font-bold mb-4">Product Not Found</h1>
          <Link to="/Market" className="text-purple-600 hover:underline">
            Return to Marketplace
          </Link>
        </main>
        <Footer />
      </div>
    );
  }

  const discountedPrice = calculateDiscountedPrice(product.price, product.discount);

  return (
    <div className="flex flex-col min-h-screen bg-gradient-to-b from-white to-gray-100">
      <LandingHeader />

      <main className="flex-1 container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="mb-6 text-sm">
          <Link to="/Market" className="text-purple-600 hover:underline">Marketplace</Link>
          <span className="mx-2 text-gray-500">/</span>
          <span className="text-gray-500">{product.category || 'Product'}</span>
          <span className="mx-2 text-gray-500">/</span>
          <span className="text-gray-700">{product.title}</span>
        </nav>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Product Images */}
          <div className="space-y-4">
            <div className="relative bg-white rounded-xl shadow-md overflow-hidden">
              {images.length > 0 ? (
                <>
                  <img
                    src={images[selectedImageIndex]}
                    alt={product.title}
                    className="w-full h-96 object-cover"
                    loading="lazy"
                  />
                  {images.length > 1 && (
                    <>
                      <button
                        onClick={() => setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length)}
                        className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg"
                        aria-label="Previous image"
                      >
                        <FaChevronLeft />
                      </button>
                      <button
                        onClick={() => setSelectedImageIndex((prev) => (prev + 1) % images.length)}
                        className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white p-2 rounded-full shadow-lg"
                        aria-label="Next image"
                      >
                        <FaChevronRight />
                      </button>
                    </>
                  )}
                </>
              ) : (
                <div className="w-full h-96 flex items-center justify-center bg-gray-200 text-gray-400">
                  No Image Available
                </div>
              )}
              {product.discount > 0 && (
                <div className="absolute top-4 right-4 bg-red-500 text-white px-3 py-1 rounded-full text-sm font-bold">
                  -{product.discount}% OFF
                </div>
              )}
            </div>

            {/* Thumbnail Gallery */}
            {images.length > 1 && (
              <div className="flex gap-2 overflow-x-auto">
                {images.map((img, index) => (
                  <button
                    key={index}
                    onClick={() => setSelectedImageIndex(index)}
                    className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 ${selectedImageIndex === index ? 'border-purple-600' : 'border-gray-200'
                      }`}
                  >
                    <img
                      src={img}
                      alt={`${product.title} ${index + 1}`}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Product Info */}
          <div className="space-y-6">
            <div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2">{product.title}</h1>
              {product.resellerName && (
                <div className="flex items-center gap-2 mb-4">
                  <FaStore className="text-gray-500" />
                  <span className="text-gray-600">
                    Sold by <span className="font-semibold text-purple-600">{product.resellerName}</span>
                  </span>
                </div>
              )}
              {product.rating > 0 && (
                <div className="flex items-center gap-2 mb-4">
                  <div className="flex items-center">
                    {[...Array(5)].map((_, i) => (
                      <FaStar
                        key={i}
                        className={i < Math.floor(product.rating) ? 'text-yellow-400' : 'text-gray-300'}
                      />
                    ))}
                  </div>
                  <span className="text-gray-600">
                    {product.rating.toFixed(1)} ({product.reviewCount || 0} reviews)
                  </span>
                </div>
              )}
            </div>

            <div className="border-t border-b border-gray-200 py-6">
              <div className="flex items-baseline gap-4 mb-4">
                {product.discount > 0 ? (
                  <>
                    <span className="text-4xl font-bold text-purple-600">
                      ₹{discountedPrice.toFixed(2)}
                    </span>
                    <span className="text-2xl text-gray-400 line-through">
                      ₹{product.price}
                    </span>
                    <span className="text-lg text-red-600 font-semibold">
                      Save ₹{(product.price - discountedPrice).toFixed(2)}
                    </span>
                  </>
                ) : (
                  <span className="text-4xl font-bold text-purple-600">
                    ₹{product.price}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-6 text-sm text-gray-600">
                <div className={`flex items-center gap-2 ${product.stock > 0 ? 'text-green-600' : 'text-red-600'}`}>
                  <div className={`w-3 h-3 rounded-full ${product.stock > 0 ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  {product.stock > 0 ? `In Stock (${product.stock} available)` : 'Out of Stock'}
                </div>
                {product.sku && (
                  <span>SKU: {product.sku}</span>
                )}
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <h3 className="font-semibold mb-2">Description</h3>
                <p className="text-gray-700 leading-relaxed whitespace-pre-line">
                  {product.description || 'No description available.'}
                </p>
              </div>

              {product.tags && product.tags.length > 0 && (
                <div>
                  <h3 className="font-semibold mb-2">Tags</h3>
                  <div className="flex flex-wrap gap-2">
                    {product.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Quantity Selector */}
            <div className="flex items-center gap-4">
              <label className="font-semibold">Quantity:</label>
              <div className="flex items-center border border-gray-300 rounded-lg">
                <button
                  onClick={() => setQuantity(Math.max(1, quantity - 1))}
                  className="px-4 py-2 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  disabled={quantity <= 1}
                  aria-label="Decrease quantity"
                >
                  -
                </button>
                <input
                  type="number"
                  value={quantity}
                  onChange={(e) => {
                    const val = parseInt(e.target.value) || 1;
                    setQuantity(Math.max(1, Math.min(val, product.stock)));
                  }}
                  className="w-16 text-center border-0 focus:ring-2 focus:ring-purple-500"
                  min="1"
                  max={product.stock}
                  aria-label="Product quantity"
                />
                <button
                  onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                  className="px-4 py-2 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  disabled={quantity >= product.stock}
                  aria-label="Increase quantity"
                >
                  +
                </button>
              </div>
              <span className="text-sm text-gray-600">
                (Max: {product.stock})
              </span>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <button
                onClick={addToCart}
                disabled={product.stock === 0}
                className={`flex-1 flex items-center justify-center gap-2 px-6 py-3 rounded-lg font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${product.stock > 0
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  }`}
                aria-label={`Add ${product.title} to cart`}
              >
                <FaShoppingCart />
                Add to Cart
              </button>
              <button
                onClick={() => setIsWishlisted(!isWishlisted)}
                className={`px-6 py-3 rounded-lg border-2 font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 ${isWishlisted
                  ? 'bg-red-50 border-red-500 text-red-600'
                  : 'border-gray-300 text-gray-700 hover:border-gray-400'
                  }`}
                aria-label={isWishlisted ? 'Remove from wishlist' : 'Add to wishlist'}
              >
                <FaHeart className={isWishlisted ? 'fill-current' : ''} />
              </button>
              <button
                onClick={handleShare}
                className="px-6 py-3 rounded-lg border-2 border-gray-300 text-gray-700 hover:border-gray-400 font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2"
                aria-label="Share product"
              >
                <FaShareAlt />
              </button>
            </div>

            {/* Product Features */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-6 border-t border-gray-200">
              <div className="flex items-start gap-3">
                <FaTruck className="text-purple-600 text-xl mt-1" />
                <div>
                  <h4 className="font-semibold">Free Shipping</h4>
                  <p className="text-sm text-gray-600">On orders over ₹500</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <FaUndo className="text-purple-600 text-xl mt-1" />
                <div>
                  <h4 className="font-semibold">Easy Returns</h4>
                  <p className="text-sm text-gray-600">{product.returnPolicy || '30 days return'}</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <FaShieldAlt className="text-purple-600 text-xl mt-1" />
                <div>
                  <h4 className="font-semibold">Warranty</h4>
                  <p className="text-sm text-gray-600">{product.warranty || '1 year warranty'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Similar Products */}
        {relatedProducts.length > 0 && (
          <div className="mt-12">
            <h2 className="text-2xl font-bold mb-6">Similar Products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {relatedProducts.map(relatedProduct => {
                const relatedDiscountedPrice = calculateDiscountedPrice(relatedProduct.price, relatedProduct.discount);
                return (
                  <Link
                    key={relatedProduct._id}
                    to={`/product/${relatedProduct._id}`}
                    className="bg-white rounded-xl shadow-md hover:shadow-xl transition-shadow overflow-hidden"
                  >
                    <div className="relative h-48 bg-gray-200">
                      {relatedProduct.image ? (
                        <img
                          src={relatedProduct.image}
                          alt={relatedProduct.title}
                          className="w-full h-full object-cover"
                          loading="lazy"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-gray-400">
                          No Image
                        </div>
                      )}
                      {relatedProduct.discount > 0 && (
                        <div className="absolute top-2 right-2 bg-red-500 text-white px-2 py-1 rounded text-sm font-bold">
                          -{relatedProduct.discount}%
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-bold text-lg mb-2 line-clamp-2">{relatedProduct.title}</h3>
                      <div className="flex items-center justify-between">
                        {relatedProduct.discount > 0 ? (
                          <div>
                            <span className="text-xl font-bold text-purple-600">
                              ₹{relatedDiscountedPrice.toFixed(2)}
                            </span>
                            <span className="text-gray-400 line-through ml-2 text-sm">
                              ₹{relatedProduct.price}
                            </span>
                          </div>
                        ) : (
                          <span className="text-xl font-bold text-purple-600">
                            ₹{relatedProduct.price}
                          </span>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </main>

      <QuickActions />
      <Footer />
    </div>
  );
};

export default ProductDetail;

